/**
 * usePlatformResolve — React Query hook wrapping GET /api/me/platform-resolve.
 *
 * Returns {person, memberships, workspaces, permissions, is_super_admin}
 * from the Identity Registry. Cached for 60s. Used by the sidebar &
 * WorkspaceSwitcher to render dynamic, role-aware menus tied to the
 * central Person record.
 */
import { useQuery } from '@tanstack/react-query';
import { getToken } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function usePlatformResolve() {
  const q = useQuery({
    queryKey: ['platform-resolve'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/me/platform-resolve`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    },
    staleTime: 60_000,
    retry: 1,
  });
  const data = q.data || {};
  return {
    ...q,
    person: data.person || null,
    memberships: data.memberships || [],
    workspaces: data.workspaces || [],
    permissions: data.permissions || [],
    isSuperAdmin: !!data.is_super_admin,
    // Fast lookups
    hasWorkspace: (key) => !!(data.is_super_admin) || (data.workspaces || []).some((w) => w.key === key),
    hasPermission: (perm) => (data.permissions || []).includes(perm) || !!data.is_super_admin,
  };
}
